import 'moment/locale/pt-br';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import {
  MatTimepicker,
  MatTimepickerModule,
} from '@angular/material/timepicker';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import _moment from 'moment';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import { Aviso } from '../../../models/Aviso';
import { EmpresaVinculo } from '../../../models/EmpresaVinculo';
import { GrupoEmpresa } from '../../../models/GrupoEmpresa';
import { Pessoa } from '../../../models/Pessoa';
import { UsuarioDetalhado } from '../../../models/UsuarioDetalhado';
import { AuthService } from '../../../services/auth/auth.service';
import { AvisoService } from '../../../services/aviso/aviso.service';
import { EmpresasService } from '../../../services/empresa/empresas.service';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { UsuariosService } from '../../../services/usuario/usuarios.service';
import { ModalFinalizacaoComponent } from '../../shared/modal-finalizacao/modal-finalizacao.component';
import { SelecionarEmpresasComponent } from '../dialogs/selecionar-empresas.component/selecionar-empresas.component';
import { SelecionarGrupoComponent } from '../dialogs/selecionar-grupo.component/selecionar-grupo.component';
import { SelecionarPessoasComponent } from '../dialogs/selecionar-pessoas.component/selecionar-pessoas.component';
import { SelecionarUsuariosComponent } from '../dialogs/selecionar-usuarios.component/selecionar-usuarios.component';
import axiosClient from '../../../../shared/axios/axios-client';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';

const moment = _moment;

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-criar-aviso',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatIconModule,
    PickerModule,
  ],
  templateUrl: './criar-aviso.component.html',
  styleUrl: './criar-aviso.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    provideMomentDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    provideNativeDateAdapter(),
  ],
})
export class CriarAvisoComponent implements OnInit {
  @ViewChild('hora') hora!: MatTimepicker<any>;

  modoEdicao = false;
  avisoId?: number;

  minData!: Date;
  maxData!: Date;
  novoAviso!: Aviso;

  mostrarEmojiPickerTitulo = false;
  mostrarEmojiPickerConteudo = false;

  destinatariosVisiveis = {
    grupos: false,
    empresas: false,
    pessoas: false,
    usuarios: false,
  };

  gruposList: any[] = [];
  empresasList: any[] = [];
  usuariosList: any[] = [];
  pessoasList: any[] = [];

  gruposSelecionados: any[] = [];
  empresasSelecionadas: any[] = [];
  idsEmpresasVinculadas: number[] = [];
  pessoasSelecionadas: any[] = [];
  usuariosSelecionados: any[] = [];

  usuariosVinculadosAviso: number[] = [];
  pessoasVinculadasAviso: number[] = [];

  form!: FormGroup;

  constructor(
    private router: Router,
    readonly route: ActivatedRoute,
    private dialog: MatDialog,
    private avisoService: AvisoService,
    private empresasService: EmpresasService,
    private pessoasService: PessoasService,
    private usuariosService: UsuariosService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    const adapter = inject(DateAdapter);
    adapter.setLocale('pt-BR');
  }

  async ngOnInit(): Promise<void> {
    this.minData = new Date();
    this.maxData = new Date(this.minData);
    this.maxData.setMonth(this.maxData.getMonth() + 1);

    this.form = this.fb.group(
      {
        titulo: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(100),
          ],
        ],
        conteudo: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(1000),
          ],
        ],
        pessoas: [[]],
        usuarios: [[]],
        grupos: [[]],
        empresas: [[]],
        data: [null, Validators.required],
        hora: [null, [Validators.required, this.horaMinValidator.bind(this)]],
        camposSelecao: this.fb.group({
          grupos: [false],
          empresas: [false],
          pessoas: [false],
          usuarios: [false],
        }),
      },
      { validators: this.peloMenosUmSelecionadoValidator }
    );

    this.form.get('data')?.valueChanges.subscribe(() => {
      this.form.get('hora')?.updateValueAndValidity();
    });

    this.form.get('camposSelecao')?.valueChanges.subscribe((val) => {
      this.destinatariosVisiveis = {
        grupos: val.grupos,
        empresas: val.empresas,
        pessoas: val.pessoas,
        usuarios: val.usuarios,
      };

      if (!val.grupos) this.gruposSelecionados = [];
      if (!val.empresas) this.empresasSelecionadas = [];
      if (!val.pessoas) this.pessoasSelecionadas = [];
      if (!val.usuarios) this.usuariosSelecionados = [];
      this.cdr.markForCheck();
    });

    await this.verificarModo();
    clearPaginas();
    pushPaginaAtiva({
      nome: 'Home',
      rota: '/home',
    });
    pushPaginaAtiva({
      nome: 'Avisos cadastrados',
      rota: '/avisos',
    });
    let nomeRota: string;
    this.modoEdicao
      ? (nomeRota = 'Editar aviso')
      : (nomeRota = 'Cadastrar aviso');
    pushPaginaAtiva({
      nome: nomeRota,
      rota: '',
    });
    this.cdr.detectChanges();
  }

  horaMinValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.parent) return null;

    const momentoAtual = new Date();
    const dataHoraSelecionada = this.combinarDataHora();

    if (!dataHoraSelecionada) return null;

    if (dataHoraSelecionada < momentoAtual) {
      momentoAtual.setMinutes(momentoAtual.getMinutes() + 5);
      return dataHoraSelecionada < momentoAtual ? { horaInvalida: true } : null;
    }

    return null;
  }

  peloMenosUmSelecionadoValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const algumaListaPreenchida =
      this.pessoasSelecionadas.length > 0 ||
      this.usuariosSelecionados.length > 0 ||
      this.empresasSelecionadas.length > 0 ||
      this.gruposSelecionados.length > 0;

    return algumaListaPreenchida ? null : { nenhumDestinatario: true };
  };

  async verificarModo() {
    const id = this.route.snapshot.paramMap.get('id');
    // Editar
    if (id) {
      this.modoEdicao = true;
      this.avisoId = +id;
      await this.carregarAviso(this.avisoId);
    }

    // Criar
    else {
      const grupos = await firstValueFrom(
        this.empresasService.buscarGruposVinculadosAoUsuario()
      );
      this.gruposList = grupos.map((g: any) => ({
        idGrupo: g.id,
        nome: g.nome,
        buscaUsuarios: false,
        buscaPessoas: false,
        selecionado: false,
      }));

      const empresas = await firstValueFrom(
        this.empresasService.buscarEmpresasVinculadasAoUsuario()
      );
      this.empresasList = empresas.map((e: any) => ({
        idGrupo: e.idGrupo,
        idEmpresa: e.idEmpresa,
        nome: e.nomeFantasia,
        buscaPessoas: false,
        buscaUsuarios: false,
        selecionado: false,
      }));
    }
  }

  async carregarAviso(id: number) {
    const aviso: any = await lastValueFrom(
      this.avisoService.getAvisoDetalhesById(id)
    );
    if (!aviso) return;

    // Data e hora
    const dataObj = aviso.dataAgendamento
      ? new Date(aviso.dataAgendamento)
      : null;

    const dataLocal = dataObj
      ? new Date(dataObj.getFullYear(), dataObj.getMonth(), dataObj.getDate())
      : null;

    // Mantém apenas os selecionados
    this.gruposList = aviso.grupos;
    this.gruposSelecionados = aviso.grupos
      .filter((e: any) => e.selecionado === true)
      .map((g: any) => ({ ...g }));

    this.empresasList = aviso.empresas;
    this.empresasSelecionadas = aviso.empresas
      .filter((e: any) => e.selecionado === true)
      .map((e: any) => ({ ...e }));

    this.pessoasList = aviso.pessoas;
    this.pessoasSelecionadas = aviso.pessoas
      .filter((p: any) => p.selecionado === true)
      .map((p: any) => ({ ...p }));

    this.usuariosList = aviso.usuarios;
    this.usuariosSelecionados = aviso.usuarios
      .filter((u: any) => u.selecionado === true)
      .map((u: any) => ({ ...u }));

    // Visibilidade
    this.destinatariosVisiveis = {
      grupos: this.gruposSelecionados.length > 0,
      empresas: this.empresasSelecionadas.length > 0,
      pessoas: this.pessoasSelecionadas.length > 0,
      usuarios: this.usuariosSelecionados.length > 0,
    };

    // Preencher form
    this.form.patchValue({
      titulo: aviso.titulo,
      conteudo: aviso.mensagem,
      grupos: aviso.grupos,
      empresas: aviso.empresas,
      pessoas: aviso.pessoas,
      usuarios: aviso.usuarios,
      data: dataLocal,
      hora: dataObj,
      camposSelecao: { ...this.destinatariosVisiveis },
    });
  }

  adicionarEmojiTitulo(event: any) {
    const valorAtual = this.form.get('titulo')?.value || '';
    this.form.get('titulo')?.setValue(valorAtual + event.emoji.native);
    this.mostrarEmojiPickerTitulo = false;
  }

  adicionarEmojiConteudo(event: any) {
    const valorAtual = this.form.get('conteudo')?.value || '';
    this.form.get('conteudo')?.setValue(valorAtual + event.emoji.native);
    this.mostrarEmojiPickerConteudo = false;
  }

  selecionarGruposDeEmpresas() {
    const dialogRef = this.dialog.open(SelecionarGrupoComponent, {
      width: '900px',
      height: '600px',
      data: {
        grupos: this.gruposList,
        selecionados: this.gruposSelecionados,
      },
    });

    dialogRef.afterClosed().subscribe((gruposSelecionados) => {
      if (gruposSelecionados) {
        this.gruposSelecionados = gruposSelecionados;

        // Processamento para USUÁRIOS
        const algumGrupoBuscaUsuarios = gruposSelecionados.some(
          (g: any) => g.buscaUsuarios
        );

        this.usuariosSelecionados = this.usuariosSelecionados.filter((u) => {
          const grupoDoUsuario = gruposSelecionados.find(
            (g: any) => g.idGrupo === u.idGrupo
          );

          // Mantém usuário se:
          // 1. Não pertence a nenhum grupo selecionado OU
          // 2. Pertence a grupo sem buscaUsuarios ativada
          return !grupoDoUsuario || !grupoDoUsuario.buscaUsuarios;
        });

        // Marca usuários como bloqueados
        this.usuariosSelecionados.forEach((u) => {
          u.bloqueado = gruposSelecionados.some(
            (g: any) => g.idGrupo === u.idGrupo && g.buscaUsuarios
          );
        });

        // Processamento para PESSOAS
        const algumGrupoBuscaPessoas = gruposSelecionados.some(
          (g: any) => g.buscaPessoas
        );

        this.pessoasSelecionadas = this.pessoasSelecionadas.filter((p) => {
          const grupoDaPessoa = gruposSelecionados.find(
            (g: any) => g.idGrupo === p.idGrupo
          );

          // Mantém pessoa se:
          // 1. Não pertence a nenhum grupo selecionado OU
          // 2. Pertence a grupo sem buscaPessoas ativada
          return !grupoDaPessoa || !grupoDaPessoa.buscaPessoas;
        });

        // Marca pessoas como bloqueadas
        this.pessoasSelecionadas.forEach((p) => {
          p.bloqueado = gruposSelecionados.some(
            (g: any) => g.idGrupo === p.idGrupo && g.buscaPessoas
          );
        });
      }

      this.form.updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  selecionarEmpresas() {
    const dialogRef = this.dialog.open(SelecionarEmpresasComponent, {
      width: '900px',
      height: '600px',
      data: {
        empresas: this.empresasList,
        grupos: this.gruposSelecionados,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const [
          empresasSelecionadasModal,
          empresasBloqueadas,
          idsEmpresasVinculadas,
        ] = result;
        const idsModal = empresasSelecionadasModal.map((e: any) => e.idEmpresa);

        const outrasEmpresas = this.empresasSelecionadas.filter(
          (e) => !idsModal.includes(e.idEmpresa)
        );

        this.empresasSelecionadas = [
          ...outrasEmpresas,
          ...empresasSelecionadasModal,
        ];
        this.idsEmpresasVinculadas = idsEmpresasVinculadas;
        const idsEmpresasSelecionadas = this.empresasSelecionadas.map(
          (e) => e.idEmpresa
        );

        this.pessoasSelecionadas = this.pessoasSelecionadas.filter((p) =>
          idsEmpresasSelecionadas.includes(p.idEmpresa)
        );

        this.usuariosSelecionados = this.usuariosSelecionados.filter((u) =>
          idsEmpresasSelecionadas.includes(u.idEmpresa)
        );

        if (this.empresasSelecionadas.some((e) => e.buscaPessoas === true)) {
          this.pessoasSelecionadas = [];
        }

        if (this.empresasSelecionadas.some((e) => e.buscaUsuarios === true)) {
          this.usuariosSelecionados = [];
        }
      }

      this.form.updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  async buscarEmpresasVinculadas() {
    const observableEmpresas =
      this.empresasService.buscarEmpresasVinculadasAoUsuario();
    const arrayEmpresas = await lastValueFrom(observableEmpresas);
    this.idsEmpresasVinculadas = arrayEmpresas
      .filter((e) => e.idEmpresa)
      .map((e) => e.idEmpresa);
  }

  async selecionarPessoas() {
    await this.buscarEmpresasVinculadas();

    const dialogRef = this.dialog.open(SelecionarPessoasComponent, {
      width: '900px',
      height: '600px',
      data: {
        empresas: this.empresasSelecionadas,
        grupos: this.gruposSelecionados,
        pessoas: this.pessoasSelecionadas,
        empresasVinculadas: this.idsEmpresasVinculadas,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const [pessoasSelecionadas, idsPessoas] = result;
        this.pessoasSelecionadas = pessoasSelecionadas;
        this.pessoasVinculadasAviso = idsPessoas;
      }
    });

    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  async selecionarUsuarios() {
    await this.buscarEmpresasVinculadas();

    const dialogRef = this.dialog.open(SelecionarUsuariosComponent, {
      width: '900px',
      height: '600px',
      data: {
        empresas: this.empresasSelecionadas,
        grupos: this.gruposSelecionados,
        pessoas: this.pessoasSelecionadas,
        usuarios: [...this.usuariosSelecionados], // Pass a copy
        empresasVinculadas: this.idsEmpresasVinculadas,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.usuariosSelecionados = result[0];
      }
    });

    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  excluirGrupo(id: number) {
    this.gruposSelecionados = this.gruposSelecionados.filter(
      (grupo) => grupo.idGrupo !== id
    );
    this.form.updateValueAndValidity();
  }

  excluirEmpresa(id: number) {
    this.empresasSelecionadas = this.empresasSelecionadas.filter(
      (empresa) => empresa.idEmpresa !== id
    );

    this.empresasList = this.empresasList.map((empresa) => {
      if (empresa.idEmpresa === id) {
        return {
          ...empresa,
          selecionado: false,
          buscaUsuarios: false,
          buscaPessoas: false,
        };
      }
      return empresa;
    });

    this.form.updateValueAndValidity();
  }

  excluirPessoa(id: number) {
    this.pessoasSelecionadas = this.pessoasSelecionadas.filter(
      (pessoa) => pessoa.idPessoa !== id
    );
    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  excluirUsuario(id: number) {
    this.usuariosSelecionados = this.usuariosSelecionados.filter(
      (usuario) => (usuario.id ?? usuario.idUsuario) !== id
    );
    this.form.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  get formularioValido(): boolean {
    return this.form.valid;
  }

  get formularioParcialmentePreenchido(): boolean {
    const titulo = this.form.get('titulo')?.value?.trim() || '';
    const conteudo = this.form.get('conteudo')?.value?.trim() || '';
    const temDestinatarios =
      this.pessoasSelecionadas.length > 0 ||
      this.usuariosSelecionados.length > 0 ||
      this.empresasSelecionadas.length > 0 ||
      this.gruposSelecionados.length > 0;
    return titulo.length > 5 && conteudo.length > 5 && temDestinatarios;
  }

  formatarDataAgendamento(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');

    const dia = pad(date.getDate());
    const mes = pad(date.getMonth() + 1);
    const ano = date.getFullYear();
    const horas = pad(date.getHours());
    const minutos = pad(date.getMinutes());
    const segundos = pad(date.getSeconds());

    return `${dia}/${mes}/${ano}-${horas}:${minutos}:${segundos}`;
  }

  combinarDataHora(): Date | null {
    const dataSelecionada = this.form.get('data')?.value;
    const horaSelecionada = this.form.get('hora')?.value;

    if (!dataSelecionada || !horaSelecionada) return null;

    const data = new Date(dataSelecionada);
    const hora = new Date(horaSelecionada);

    data.setHours(hora.getHours());
    data.setMinutes(hora.getMinutes());
    data.setSeconds(0);
    data.setMilliseconds(0);

    return data;
  }

  async buscarPessoasAPartirDasEmpresasEGruposSelecionados() {
    const pessoasSet = new Set<number>();
    const idsEmpresas = this.empresasSelecionadas.map((e) => e.idEmpresa);
    const idsGrupos = this.gruposSelecionados
      .filter((g) => g.buscaPessoas)
      .map((g) => g.id);

    if (idsEmpresas.length > 0) {
      const pessoasEmpresa =
        await this.pessoasService.buscarPessoasPorEmpresaEGrupo(
          idsEmpresas,
          null
        );
      pessoasEmpresa.forEach((p) => pessoasSet.add(p.idPessoa));
    }

    if (idsGrupos.length > 0) {
      const pessoasGrupo =
        await this.pessoasService.buscarPessoasPorEmpresaEGrupo(
          null,
          idsGrupos
        );
      pessoasGrupo.forEach((p) => pessoasSet.add(p.idPessoa));
    }

    this.novoAviso.pessoas = Array.from(pessoasSet);
  }

  async buscarUsuariosAPartirDasEmpresasEGruposSelecionados() {
    const usuariosSet = new Set<number>();
    const idsEmpresas = this.empresasSelecionadas.map((e) => e.idEmpresa);
    const idsGrupos = this.gruposSelecionados
      .filter((g) => g.buscaUsuarios)
      .map((g) => g.id);

    if (idsEmpresas.length > 0) {
      const usuariosEmpresa =
        await this.usuariosService.buscarUsuariosPorEmpresasEGrupos(
          idsEmpresas,
          null
        );
      usuariosEmpresa.forEach((u) => usuariosSet.add(u.id));
    }

    if (idsGrupos.length > 0) {
      const usuariosGrupo =
        await this.usuariosService.buscarUsuariosPorEmpresasEGrupos(
          null,
          idsGrupos
        );
      usuariosGrupo.forEach((u) => usuariosSet.add(u.id));
    }

    this.novoAviso.usuarios = Array.from(usuariosSet);
  }

  sairSemSalvar() {
    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: {
        isAviso: true,
        titulo: 'Sair sem salvar',
        conteudo:
          'Ao sair sem salvar, você perderá todo o progresso ' +
          'e as informações preenchidas até o momento. Deseja sair?',
        possuiAcoes: true,
        acao: 'Sair',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.router.navigate(['/avisos']);
      }
    });
  }

  async salvarEAgendar(status: string) {
    const idUsuario = this.authService.getUsuarioId();

    const { titulo, conteudo, data, hora } = this.form.value;

    this.novoAviso = new Aviso();
    this.novoAviso.titulo = titulo.trim();
    this.novoAviso.mensagem = conteudo.trim();
    this.novoAviso.criadoPor = idUsuario!;
    this.novoAviso.status = status;

    if (status === 'AGENDADO') {
      const data = this.combinarDataHora();
      if (!data) {
        console.error('Data ou hora inválida');
        return;
      }
      this.novoAviso.dataAgendamento = this.formatarDataAgendamento(data);
    }

    const _grupos = this.gruposSelecionados.map((g) => {
      return {
        id: g.idGrupo ?? g.id,
        buscaPessoas: g.buscaPessoas,
        buscaUsuarios: g.buscaUsuarios,
      };
    });

    const _empresas = this.empresasSelecionadas.map((e) => {
      return {
        id: e.idEmpresa,
        buscaPessoas: e.buscaPessoas,
        buscaUsuarios: e.buscaUsuarios,
      };
    });

    const _usuarios = this.usuariosSelecionados.map((u) => {
      return {
        id: u.id ?? u.idUsuario,
      };
    });

    const _pessoas = this.pessoasSelecionadas.map((p) => {
      return {
        id: p.idPessoa,
      };
    });

    const _dataAgendamento = this.combinarDataHora();
    if (!_dataAgendamento) {
      console.error('Data ou hora inválida');
      return;
    }
    this.novoAviso.dataAgendamento =
      this.formatarDataAgendamento(_dataAgendamento);

    let _body = {
      id: this.avisoId ?? null,
      titulo: titulo,
      mensagem: conteudo,
      dataAgendamento: this.novoAviso.dataAgendamento,
      status: status,
      grupos: _grupos,
      empresas: _empresas,
      usuarios: _usuarios,
      pessoas: _pessoas,
    };

    if (!this.formularioValido && status === 'AGENDADO') return;
    if (!this.formularioParcialmentePreenchido && status === 'RASCUNHO') return;

    status === 'AGENDADO'
      ? this.abrirModalFinalizacao(_body)
      : this.abrirModalRascunho(_body);
  }

  abrirModalFinalizacao(body: any) {
    const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
      width: '600px',
      data: {
        isAviso: true,
        titulo: 'Confirmar e cadastrar aviso',
        conteudo:
          'Ao clicar em confirmar, o aviso será cadastrado e disparado ' +
          'para os destinatários selecionados conforme a sua configuração. ' +
          'Deseja confirmar e cadastrar?',
        possuiAcoes: true,
        acao: 'Confirmar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // Criar aviso
      if (result === true && !this.modoEdicao) {
        this.avisoService.criarOuAtualizarAviso(body).subscribe({
          next: (resposta) => {
            this.snackBarService.success('Aviso cadastrado com sucesso.');
            this.router.navigate(['/avisos']);
          },
          error: (erro) => {
            this.snackBarService.error('Erro ao criar aviso.');
          },
        });
      }
      // Editar aviso
      else if (result === true && this.modoEdicao && this.avisoId) {
        this.avisoService.criarOuAtualizarAviso(body).subscribe({
          next: (resposta) => {
            this.snackBarService.success('Aviso editado com sucesso.');
            this.router.navigate(['/avisos']);
          },
          error: (erro) => {
            this.snackBarService.error('Erro ao criar aviso.');
          },
        });
      }
    });
  }

  abrirModalRascunho(body: any) {
    const avisoObservable =
      this.modoEdicao && this.avisoId
        ? this.avisoService.editarAviso(body, this.avisoId)
        : this.avisoService.criarOuAtualizarAviso(body);

    avisoObservable.subscribe({
      next: (resposta) => {
        const dialogRef = this.dialog.open(ModalFinalizacaoComponent, {
          width: '600px',
          data: {
            isAviso: true,
            titulo: 'Salvo como rascunho',
            conteudo:
              'Seu aviso foi salvo como rascunho. As informações atuais serão armazenadas, ' +
              'você poderá retornar e finalizar o preenchimento a qualquer momento, ' +
              'sem perder o que já foi registrado.',
            possuiAcoes: false,
          },
        });

        dialogRef.afterClosed().subscribe((r) => {
          this.snackBarService.success('Rascunho cadastrado com sucesso.');
          this.router.navigate(['/avisos']);
        });
      },
      error: (erro) => {
        console.error('Erro ao criar aviso:', erro);
      },
    });
  }

  fecharTimepicker() {
    if (this.hora) {
      this.hora.close();
    }
  }
}
